import * as signalR from '@microsoft/signalr';
import ApiService from './ApiService';

// В режиме разработки используем localhost напрямую для SignalR WebSocket
// В production используем текущий хост
const SIGNALR_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL?.replace('/api', '') || window.location.origin)
  : 'http://localhost:5000';

console.log('🔧 SignalR URL:', SIGNALR_URL);

let connection = null;

// Проверить готовность соединения
const isConnected = () => {
  return connection && connection.state === signalR.HubConnectionState.Connected;
};

const SignalRService = {
  // Подключиться к SignalR Hub
  connect: async () => {
    if (connection?.state === signalR.HubConnectionState.Connected) {
      console.log('✓ SignalR уже подключен');
      return connection;
    }

    connection = new signalR.HubConnectionBuilder()
      .withUrl(`${SIGNALR_URL}/hub/messenger`, {
        accessTokenFactory: () => localStorage.getItem('user_token') || ''
      })
      .withAutomaticReconnect()
      .withHubProtocol(new signalR.JsonHubProtocol())
      .build();

    connection.on('NewMessage', (data) => {
      console.log('📨 Новое сообщение:', data);
      // Событие будет обработано в App.js
    });

    connection.on('ChatUpdated', (data) => {
      console.log('💬 Чат обновлен:', data);
      // Событие будет обработано в App.js
    });

    connection.onreconnected(() => {
      console.log('✓ SignalR переподключился');
    });

    connection.onclose(() => {
      console.log('❌ SignalR отключился');
    });

    try {
      await connection.start();
      console.log('✓ SignalR подключен');
      // Даем соединению время стабилизироваться
      await new Promise(resolve => setTimeout(resolve, 100));
      return connection;
    } catch (err) {
      console.error('✗ Ошибка подключения SignalR:', err);
      throw err;
    }
  },

  // Получить все чаты
  getChats: async () => {
    // Если SignalR не подключен, используем HTTP
    if (!isConnected()) {
      console.warn('SignalR не подключен, используем HTTP API');
      return await ApiService.getChats();
    }
    
    try {
      const chats = await connection.invoke('GetChats');
      console.log('✓ Чаты получены через SignalR:', chats);
      return chats;
    } catch (err) {
      console.warn('⚠ Ошибка при получении чатов через SignalR, fallback на HTTP:', err);
      // Fallback на HTTP
      return await ApiService.getChats();
    }
  },

  // Получить сообщения чата
  getMessages: async (chatId, limit = 50, offset = 0) => {
    // Если SignalR не подключен, используем HTTP
    if (!isConnected()) {
      console.warn('SignalR не подключен, используем HTTP API');
      return await ApiService.getMessages(chatId, limit, offset);
    }
    
    try {
      const messages = await connection.invoke('GetMessages', chatId, limit, offset);
      return messages;
    } catch (err) {
      console.warn('⚠ Ошибка при получении сообщений через SignalR, fallback на HTTP:', err);
      // Fallback на HTTP
      return await ApiService.getMessages(chatId, limit, offset);
    }
  },

  // Подписаться на обновления конкретного чата
  subscribeToChat: async (chatId) => {
    if (!isConnected()) {
      console.warn('SignalR не подключен, пропускаю подписку на чат');
      return;
    }
    try {
      await connection.invoke('SubscribeToChat', chatId);
      console.log(`✓ Подписались на чат ${chatId}`);
    } catch (err) {
      console.warn(`⚠ Не удалось подписаться на чат ${chatId}:`, err.message);
      // Не выбрасываем ошибку, чтобы приложение продолжало работать
    }
  },

  // Отписаться от обновлений чата
  unsubscribeFromChat: async (chatId) => {
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    try {
      await connection.invoke('UnsubscribeFromChat', chatId);
      console.log(`✓ Отписались от чата ${chatId}`);
    } catch (err) {
      console.warn(`⚠ Ошибка при отписке от чата ${chatId}:`, err.message);
    }
  },

  // Подписаться на все чаты
  subscribeToAllChats: async () => {
    if (!isConnected()) {
      console.warn('SignalR не подключен, пропускаю подписку на все чаты');
      return;
    }
    try {
      await connection.invoke('SubscribeToAllChats');
      console.log('✓ Подписались на все чаты');
    } catch (err) {
      console.warn('⚠ Ошибка при подписке на все чаты:', err.message);
    }
  },

  // Отключиться
  disconnect: async () => {
    if (connection) {
      try {
        await connection.stop();
        console.log('✓ SignalR отключен');
      } catch (err) {
        console.error('✗ Ошибка при отключении:', err);
      }
    }
  },

  // Получить текущее соединение
  getConnection: () => connection
};

export default SignalRService;
